import { useState } from "react";
import { NOT_INCLUDED, personalVsProp, sixtyFortyVsOrdinary } from "@ftr/tax-core";
import { Card, JumpToResult, MoneyField, NotIncluded, Pending, Section, WhyPanel } from "../components/ui.tsx";
import { Figure, Line, ResultCard } from "../components/figure.tsx";
import { collect } from "../lib/fields.ts";
import { pct, usd } from "../lib/format.ts";
import type { Profile } from "../lib/profile.ts";
import { AboutYou, PROP_BUSINESS_ASSUMPTION, QbiAnswer, QbiQuestion, baseFrom } from "./shared.tsx";

type Mode = "6040" | "prop";

const COPY: Record<Mode, { amountLabel: string; hint: string; a: string; b: string; win: string; lose: string }> = {
  "6040": {
    amountLabel: "Futures gain for 2026",
    hint: "Your net Section 1256 gain, Box 11 on your 1099-B",
    a: "With 60/40",
    b: "If it were ordinary income",
    win: "60/40 saves you",
    lose: "60/40 doesn't change your tax here",
  },
  prop: {
    amountLabel: "Trading profit for 2026",
    hint: "The same profit, earned two different ways",
    a: "In your personal futures account",
    b: "As prop-firm payouts",
    win: "Trading in your personal account would cost",
    lose: "Prop payouts cost no more here",
  },
};

export function Compare({ mode, profile, update }: { mode: Mode; profile: Profile; update: (p: Partial<Profile>) => void }) {
  const [amount, setAmount] = useState("");
  const [otherProp, setOtherProp] = useState("");
  const copy = COPY[mode];

  const c = collect({
    amount: { raw: amount, label: mode === "6040" ? "your futures gain" : "your profit" },
    otherProp: { raw: otherProp, label: "other prop profit", optional: true },
  });
  const b = baseFrom(profile, { otherPropNetProfit: c.values.otherProp });
  const missing = [...c.missing, ...b.missing];
  const errors = [...c.errors, ...b.errors];
  const positive = c.values.amount?.greaterThan(0);
  const ready = missing.length === 0 && errors.length === 0 && positive;
  const r = ready ? (mode === "6040" ? sixtyFortyVsOrdinary : personalVsProp)(b.base, c.values.amount) : null;

  const max = r?.state === "computed" ? Math.max(r.taxA.toNumber(), r.taxB.toNumber(), 1) : 1;

  return (
    <div className="calc">
      <Card className="inputs">
        <Section label="Your trading year">
          <MoneyField label={copy.amountLabel} value={amount} onChange={setAmount} hint={copy.hint} />
          <MoneyField label="Other prop profit this year" optional value={otherProp} onChange={setOtherProp} hint="Net of fees, if you also have prop payouts" />
        </Section>
        <AboutYou profile={profile} update={update} />
      </Card>
      {r?.state === "computed" && <JumpToResult text={`${usd(r.taxA)} vs ${usd(r.taxB)}`} />}

      <div className="results">
        {!r && <Pending missing={missing} errors={errors.length ? errors : c.values.amount && !positive ? ["the amount (use a gain above $0)"] : []} />}
        {r?.state === "invalid" && <Pending missing={[]} errors={r.errors} />}
        {r?.state === "needs_input" && <QbiQuestion profile={profile} update={update} />}
        {r?.state === "computed" && (
          <ResultCard title={mode === "6040" ? "60/40 vs ordinary" : "Personal account vs prop"}>
            <p className="result-label">{r.difference.greaterThan(0) ? (mode === "6040" ? copy.win : "Your personal account would save") : copy.lose}</p>
            {r.difference.greaterThan(0) && (
              <p className="big big--gain">
                <Figure value={r.difference} />
              </p>
            )}
            <p className="result-sub">in 2026 federal tax on {usd(c.values.amount)}, on top of your other income.</p>
            <div className="duel">
              {[
                { label: copy.a, tax: r.taxA, tone: "good" },
                { label: copy.b, tax: r.taxB, tone: "warn" },
              ].map((row) => (
                <div key={row.label} className="duel-row">
                  <span className="duel-label">{row.label}</span>
                  <span className="duel-track">
                    <span className={"duel-fill seg--" + row.tone} style={{ width: `${Math.max(2, (Math.max(0, row.tax.toNumber()) / max) * 100)}%` }} />
                  </span>
                  <span className="duel-value">{usd(row.tax)}</span>
                </div>
              ))}
            </div>
            <div className="lines lines--quiet">
              <Line label={copy.a} note="effective rate on this amount" value={pct(r.taxA.div(c.values.amount))} />
              <Line label={copy.b} note="effective rate on this amount" value={pct(r.taxB.div(c.values.amount))} />
            </div>
            {mode === "6040" ? (
              <p className="callout">60% of a futures gain is taxed at long-term capital gain rates (0%, 15% or 20%) and 40% at your regular rate, however briefly you held each trade.</p>
            ) : (
              <p className="callout">Prop payouts are usually self-employment income: no 60/40, plus self-employment tax of 15.3% up to the Social Security wage base and 2.9% above it. Prop expenses are usually deductible business expenses, and you're not risking your own capital. This compares tax only.</p>
            )}
            <WhyPanel trace={r.a.trace} title={`Why: ${copy.a.toLowerCase()}`} />
            <WhyPanel trace={r.b.trace} title={`Why: ${copy.b.toLowerCase()}`} />
            <QbiAnswer profile={profile} update={update} />
            <NotIncluded
              items={NOT_INCLUDED}
              assumedZero={[...c.assumedZero, ...b.assumedZero]}
              assumptions={[
                mode === "6040"
                  ? "The gain is from regulated futures (Section 1256 contracts) in your personal taxable account"
                  : PROP_BUSINESS_ASSUMPTION,
                ...b.assumptions,
              ]}
            />
          </ResultCard>
        )}
      </div>
    </div>
  );
}
